import { useEffect, useState } from "react";
import { AlertTriangle, Check, CreditCard, Layers, Loader2, Settings2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { modules as allModules } from "@/data/modules";
import { stripePlans, planIdFromProduct } from "@/data/stripe-plans";
import { plans } from "@/data/plans";

export default function Dashboard() {
  const { user, subscription: subInfo } = useAuth();
  const [userModules, setUserModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("user_modules")
      .select("module_id")
      .eq("user_id", user.id)
      .then(({ data }) => {
        setUserModules(data?.map((r) => r.module_id) ?? []);
        setLoading(false);
      });
  }, [user]);

  const openCustomerPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast.error("Kundenportal konnte nicht geöffnet werden.");
    } finally {
      setPortalLoading(false);
    }
  };

  // Derive plan name from subscription productId
  const planKey = subInfo.productId ? planIdFromProduct(subInfo.productId) : null;
  const planDef = planKey ? plans.find((p) => p.id === planKey) : null;
  const planName = planDef?.name ?? (subInfo.subscribed ? "Aktiv" : "Kein Plan");
  const pointsIncluded = planDef?.pointsIncluded ?? 0;

  // Status
  const statusLabel = subInfo.status === "trialing" ? "Testphase" : subInfo.subscribed ? "Aktiv" : "Inaktiv";

  // Trial banner
  const isTrialing = subInfo.status === "trialing";
  const trialEndDate = subInfo.trialEnd ? new Date(subInfo.trialEnd) : (isTrialing && subInfo.subscriptionEnd ? new Date(subInfo.subscriptionEnd) : null);
  const daysLeft = trialEndDate ? Math.max(0, Math.ceil((trialEndDate.getTime() - Date.now()) / 86400000)) : null;
  const showTrialWarning = isTrialing && daysLeft !== null && daysLeft <= 7;
  const showTrialInfo = isTrialing && daysLeft !== null && daysLeft > 7;
  const showNoPlanBanner = !subInfo.subscribed && !loading;

  // Map module IDs to display names
  const activeModuleNames = userModules.map((id) => {
    const mod = allModules.find((m) => m.id === id);
    return mod?.name ?? id;
  });

  if (loading) {
    return (
      <div className="container py-8 max-w-5xl flex justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-5xl space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground mb-1">Willkommen zurück!</h1>
        <p className="text-muted-foreground">Hier sehen Sie Ihren Plan, aktive Module und den aktuellen Status.</p>
      </div>

      {/* Trial warning banner (≤7 days) */}
      {showTrialWarning && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl border border-warning/30 bg-warning/10 p-5">
          <AlertTriangle className="h-6 w-6 text-warning shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-foreground">
              Ihre Testphase endet {daysLeft === 0 ? "heute" : `in ${daysLeft} Tag${daysLeft !== 1 ? "en" : ""}`}
            </p>
            <p className="text-sm text-muted-foreground">
              Hinterlegen Sie jetzt eine Zahlungsmethode, damit Ihr Abo nach der Testphase nahtlos weiterläuft.
            </p>
          </div>
          <Button size="sm" onClick={openCustomerPortal} disabled={portalLoading} className="bg-primary text-primary-foreground shrink-0">
            {portalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
            Zahlungsmethode verwalten
          </Button>
        </div>
      )}

      {/* Trial info banner (>7 days) */}
      {showTrialInfo && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl border border-primary/20 bg-primary/5 p-5">
          <Sparkles className="h-6 w-6 text-primary shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-foreground">
              Testphase: noch {daysLeft} Tag{daysLeft !== 1 ? "e" : ""}
            </p>
            <p className="text-sm text-muted-foreground">
              Nach Ablauf der Testphase benötigen Sie eine Zahlungsmethode, um fortzufahren. Ohne Zahlungsdaten wird das Abo nicht verlängert.
            </p>
          </div>
        </div>
      )}

      {/* No plan banner */}
      {showNoPlanBanner && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl border border-primary/20 bg-primary/5 p-5">
          <Sparkles className="h-6 w-6 text-primary shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-foreground">Starten Sie jetzt mit einem Plan</p>
            <p className="text-sm text-muted-foreground">Konfigurieren Sie Ihr System mit unserem Baukasten — 30 Tage kostenlos.</p>
          </div>
          <Button size="sm" asChild className="bg-primary text-primary-foreground shrink-0">
            <Link to="/konfigurator">Zum Konfigurator</Link>
          </Button>
        </div>
      )}

      {/* Plan card */}
      {subInfo.subscribed && (
        <div className="rounded-2xl border border-primary/20 bg-card p-6 shadow-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
              <Sparkles className="h-7 w-7 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Ihr Plan</p>
              <p className="font-display text-xl font-bold text-foreground">{planName}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">{userModules.length}</p>
              <p className="text-xs text-muted-foreground">Module aktiv</p>
            </div>
            <Button variant="outline" size="sm" onClick={openCustomerPortal} disabled={portalLoading}>
              Abo verwalten
            </Button>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Aktive Module", value: userModules.length, icon: Layers },
          { label: "Plan-Budget", value: pointsIncluded ? `${pointsIncluded} Pkt` : "—", icon: Settings2 },
          { label: "Status", value: statusLabel, icon: Check },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl border border-border bg-card p-5 shadow-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                <stat.icon className="h-4 w-4 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">{stat.label}</span>
            </div>
            <p className="font-display text-2xl font-bold text-foreground">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Active Modules */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
        <h2 className="font-display font-bold text-lg text-foreground mb-4">Aktive Module</h2>
        {userModules.length === 0 ? (
          <p className="text-muted-foreground text-sm">Noch keine Module aktiviert.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {activeModuleNames.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary border border-primary/20"
              >
                <Check className="h-3.5 w-3.5" />
                {name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
